import React, { useState, useEffect } from "react";
import {
  Link,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import {
  Eye,
  EyeOff,
  LogIn,
  AlertCircle,
  Mail,
  CheckCircle,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { getFirebaseAuthError } from "../utils/errorHandler";

const Login: React.FC = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [emailJustVerified, setEmailJustVerified] = useState(false);

  const { login, resetPassword, resendVerificationEmailToUnverified } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Check if user just verified their email
  useEffect(() => {
    if (searchParams.get("verified") === "true") {
      setEmailJustVerified(true);
    }
  }, [searchParams]);

  // Get the redirect path from location state or default to /acces
  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname ||
    "/acces";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setEmailNotVerified(false);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      // Check if this is the email not verified error
      if (err instanceof Error && err.message === "EMAIL_NOT_VERIFIED") {
        setEmailNotVerified(true);
      } else {
        const errorMessage = getFirebaseAuthError(err, t, "login");
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    try {
      await resendVerificationEmailToUnverified(email, password);
      setResendSuccess(true);
    } catch (err) {
      console.error("Error resending verification email:", err);
      setError(t.login.errors.sendEmailError);
    } finally {
      setResendLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setError(t.login.errors.enterEmailForReset);
      return;
    }

    setLoading(true);
    setError("");

    try {
      await resetPassword(email);
      setResetEmailSent(true);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t.login.errors.sendEmailError;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 bg-gray-50 flex items-center">
      <div className="container-custom">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-romanian-blue rounded-xl mb-4">
              <LogIn className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-display text-3xl font-bold text-romanian-blue">
              {t.nav.login}
            </h1>
            <p className="text-gray-600 mt-2">{t.login.title}</p>
          </div>

          {/* Form */}
          <div className="card p-8">
            {/* Email verified success message */}
            {emailJustVerified && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="text-green-800 font-medium">
                      Email verificata con successo!
                    </p>
                    <p className="text-green-700 text-sm">
                      Ora puoi accedere al tuo account.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {emailNotVerified ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t.login.emailNotVerifiedTitle}
                </h3>
                <p className="text-gray-600 mb-4">
                  {t.login.emailNotVerifiedDesc}
                </p>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-amber-800 text-sm text-left">
                      {t.register.verificationSent.checkSpam}
                    </p>
                  </div>
                </div>

                {resendSuccess ? (
                  <div className="flex items-center justify-center space-x-2 text-green-600 mb-6">
                    <CheckCircle className="w-5 h-5" />
                    <span>{t.login.verificationResent}</span>
                  </div>
                ) : (
                  <button
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className="text-romanian-blue hover:underline font-medium mb-6 disabled:opacity-50"
                  >
                    {resendLoading
                      ? t.login.resendingVerification
                      : t.login.resendVerification}
                  </button>
                )}

                <div className="border-t pt-6">
                  <button
                    onClick={() => {
                      setEmailNotVerified(false);
                      setResendSuccess(false);
                    }}
                    className="btn-primary"
                  >
                    {t.login.backToLogin}
                  </button>
                </div>
              </div>
            ) : resetEmailSent ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t.login.resetEmailSent}
                </h3>
                <p className="text-gray-600 mb-4">
                  {t.login.resetEmailDescription}
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-amber-800 text-sm text-left">
                      {t.login.resetCheckSpam}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setResetEmailSent(false)}
                  className="btn-primary"
                >
                  {t.login.backToLogin}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Message */}
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-red-700 text-sm">{error}</span>
                  </div>
                )}

                {/* Redirect Message */}
                {from !== "/" && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
                    {t.login.redirectMessage}
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="label">
                    {t.login.email}
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="input"
                    placeholder="email@esempio.com"
                  />
                </div>

                <div className="relative">
                  <label htmlFor="password" className="label">
                    {t.login.password}
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="input pr-12"
                    placeholder={t.login.password}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    className="text-sm text-romanian-blue hover:underline"
                  >
                    {t.login.forgotPassword}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="spinner w-5 h-5 border-2 border-white/30 border-t-white" />
                      <span>{t.login.loggingIn}</span>
                    </>
                  ) : (
                    <>
                      <LogIn className="w-5 h-5" />
                      <span>{t.login.loginButton}</span>
                    </>
                  )}
                </button>

                <p className="text-center text-gray-600">
                  {t.login.noAccount}{" "}
                  <Link
                    to="/register"
                    className="text-romanian-blue font-semibold hover:underline"
                  >
                    {t.login.createAccount}
                  </Link>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
